import type { RagImageItem } from "../types/ragChatTypes";
import {
  buildStepImagesByIndex,
  classifyNumberedAnswerKind,
  cleanStepDisplayText,
  parseNumberedSteps,
  stripInlineMarkdown,
  type ParsedStep,
  type StepImageBinding,
} from "./ragChatUtils";

/** One numbered callout in an exploded-view diagram legend. */
export interface CatalogLegendItem {
  number: number;
  name: string;
  description?: string;
  page?: string;
  images: StepImageBinding[];
}

const PROCEDURE_QUERY_RE =
  /\b(?:how\s+to|steps?\s+(?:for|to)|procedure\s+(?:for|to)|removing|installing|replacing|remove|install|replace|disassembl|overhaul|give\s+me\s+(?:the\s+)?steps)\b/i;

const PARTS_QUERY_PATTERNS: RegExp[] = [
  /\bcomponents?\s+(?:of|in|shown|for|that)\b/i,
  /\bparts?\s+(?:of|in|list|catalog|shown|for|that)\b/i,
  /\blist\s+(?:of\s+)?(?:the\s+)?(?:parts?|components?)\b/i,
  /\bwhat\s+are\s+the\s+(?:components?|parts?)\b/i,
  /\bshow\s+(?:me\s+)?(?:the\s+)?(?:parts?|components?)\b/i,
  /\bidentify\s+the\s+(?:parts?|components?)\b/i,
  /\bcomponents?\s+in\s+this\s+diagram\b/i,
  /\blist\s+the\s+components?\b/i,
  /\bshow\s+all\s+parts?\b/i,
  /\bassembly\s+(?:parts?|components?)\b/i,
];

/** Minimum legend entries required before showing the structured UI. */
export const MIN_CATALOG_ITEMS_FOR_UI = 3;

/** Detect user intent for a parts/components catalog query (not procedures). */
export function isPartsCatalogQuery(query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  if (PROCEDURE_QUERY_RE.test(q)) return false;
  return PARTS_QUERY_PATTERNS.some((re) => re.test(q));
}

/** Split a catalog line into display name and optional subtitle. */
export function parseCatalogItemFields(text: string): { name: string; description?: string } {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return { name: "" };

  const colonIdx = clean.indexOf(":");
  if (colonIdx > 0 && colonIdx <= 60) {
    const name = clean.slice(0, colonIdx).trim();
    const description = clean.slice(colonIdx + 1).trim();
    return { name, description: description || undefined };
  }

  const torqueMatch = clean.match(
    /^(.+?)\s+(\d+(?:\.\d+)?\s*(?:N[·\s]?m|Nm|N\.m|ft[-\s]?lb))\s*$/i
  );
  if (torqueMatch && torqueMatch[1].trim().length >= 3) {
    return {
      name: torqueMatch[1].trim(),
      description: torqueMatch[2].trim(),
    };
  }

  return { name: clean };
}

/**
 * Parse all numbered dash-list entries as diagram legend callouts.
 * Preserves original diagram numbers — does NOT filter torque/spec entries.
 *
 * Catalog row thumbnails are attached only when the part text explicitly cites
 * an image (`[p.N]` / `[img:N]`). Proportional fallback from
 * {@link buildStepImagesByIndex} must not invent associations for uncited parts.
 */
export function parseCatalogLegendFromSteps(
  steps: ParsedStep[],
  stepImagesByIndex: Record<number, StepImageBinding[]>
): CatalogLegendItem[] {
  const entries: CatalogLegendItem[] = [];

  steps.forEach((step, index) => {
    if (!step.num) return;
    if (step.marker !== "dash") return;

    const num = Number.parseInt(step.num, 10);
    if (!Number.isFinite(num)) return;

    const { name, description } = parseCatalogItemFields(step.text);
    if (!name) return;

    const pageMatch = step.text.match(/\[p\.(\d+)\]/);
    const hasExplicitImageRef =
      Boolean(pageMatch) || /\[img:\d+\]/.test(step.text);
    entries.push({
      number: num,
      name,
      description,
      page: pageMatch?.[1],
      images: hasExplicitImageRef ? stepImagesByIndex[index] ?? [] : [],
    });
  });

  return entries.sort((a, b) => a.number - b.number);
}

export function shouldUsePartsCatalogView(
  userQuery: string | undefined,
  _numberedKind: "procedure" | "spec-list" | null,
  entries: CatalogLegendItem[]
): boolean {
  if (!isPartsCatalogQuery(userQuery ?? "")) return false;
  if (entries.length < MIN_CATALOG_ITEMS_FOR_UI) return false;
  // Parts/components queries with a parsed dash legend take precedence over
  // procedure Step Cards. Classification kind is intentionally unused here.
  return true;
}

export function evaluatePartsCatalogPresentation(
  userQuery: string | undefined,
  answerText: string,
  inlineImages: Record<string, RagImageItem[]>
): { show: boolean; entries: CatalogLegendItem[] } {
  const steps = parseNumberedSteps(answerText);
  if (!steps?.some((s) => s.num)) {
    return { show: false, entries: [] };
  }

  const numberedKind = classifyNumberedAnswerKind(steps);
  const stepImagesByIndex = buildStepImagesByIndex(steps, inlineImages);
  const entries = parseCatalogLegendFromSteps(steps, stepImagesByIndex);
  const show = shouldUsePartsCatalogView(userQuery, numberedKind, entries);
  return { show, entries };
}
